// Чистые функции новой главной (business_apps#1486): вариант, порядок блоков,
// правило географии, третья линия строки, параметры фильтров.

import dayjs from 'dayjs'
import {
  HOME_VARIANTS,
  HOME_LARGE_FLEET_THRESHOLD,
  CITIES_WITH_STREETS_MAX,
  STREETS_PER_CITY_LIMIT,
  SINGLE_CITY_STREETS_LIMIT,
  MODEL_OPTIONS,
  FIRMWARE_STATUSES,
  DYNAMIC_KEYS_STATES,
  INSTALLED_SINCE_VALUES,
  FILTER_NAMES,
  HOME_V2_TOGGLE_FOR_ALL,
} from './constants'

const isVariant = (value) =>
  value === HOME_VARIANTS.V1 || value === HOME_VARIANTS.V2

// effectiveVariant = user_settings.home_variant || home_variant_default (раздел 4.2).
export const effectiveHomeVariant = (partner) => {
  if (!partner) return null
  const own = partner.user_settings?.home_variant
  if (isVariant(own)) return own
  return partner.home_variant_default === HOME_VARIANTS.V2
    ? HOME_VARIANTS.V2
    : HOME_VARIANTS.V1
}

// Пользователь ещё не выбирал вариант сам — работает дефолт компании.
export const isDefaultHomeVariant = (partner) =>
  !isVariant(partner?.user_settings?.home_variant)

// Видит ли пользователь тоггл (см. HOME_V2_TOGGLE_FOR_ALL). Без новых полей me
// (Контроль-API ещё без #1488) тоггла нет ни у кого.
export const isHomeToggleAvailable = (
  partner,
  forAll = HOME_V2_TOGGLE_FOR_ALL
) => {
  if (forAll) return true
  return (
    partner?.home_variant_default === HOME_VARIANTS.V2 ||
    partner?.user_settings?.home_variant === HOME_VARIANTS.V2
  )
}

// --- порядок блоков (раздел 3) ---

export const HOME_BLOCKS = {
  RECENT: 'recent',
  CITIES: 'cities',
  SEARCH: 'search',
  FILTERS: 'filters',
  COUNTERS: 'counters',
  LIST: 'list',
}

export const isLargeFleet = (fleetSize) =>
  Number.isFinite(Number(fleetSize)) &&
  Number(fleetSize) >= HOME_LARGE_FLEET_THRESHOLD

// До 1000 устройств карточки стоят над поиском, от 1000 — поиск сразу под
// действиями, карточки под ним. Шапка и действия всегда выше и здесь не считаются.
export const homeBlocksOrder = (fleetSize) => {
  const { RECENT, CITIES, SEARCH, FILTERS, COUNTERS, LIST } = HOME_BLOCKS
  return isLargeFleet(fleetSize)
    ? [SEARCH, RECENT, CITIES, FILTERS, COUNTERS, LIST]
    : [RECENT, CITIES, SEARCH, FILTERS, COUNTERS, LIST]
}

// При непустом запросе у крупного парка карточки сворачиваются в одну строку,
// чтобы результаты были рядом с полем.
export const shouldCollapseCards = ({ fleetSize, queryLen = 0 }) =>
  isLargeFleet(fleetSize) && queryLen > 0

// --- география (раздел 5.4) ---

export const GEO_MODES = {
  EMPTY: 'empty',
  MULTI_COUNTRY: 'multi_country',
  MULTI_CITY: 'multi_city',
  SINGLE_CITY: 'single_city',
}

const byDevicesDesc = (a, b) => (b.devices_count || 0) - (a.devices_count || 0)
const byHousesDesc = (a, b) =>
  (b.houses_count || 0) - (a.houses_count || 0) || byDevicesDesc(a, b)

const countriesWithCities = (geography) =>
  (geography?.countries || []).filter(
    (country) => (country.localities || []).length > 0
  )

export const geographyMode = (geography) => {
  const countries = countriesWithCities(geography)
  if (!geography || !geography.total_devices || countries.length === 0) {
    return GEO_MODES.EMPTY
  }
  if (countries.length > 1) return GEO_MODES.MULTI_COUNTRY
  return countries[0].localities.length > 1
    ? GEO_MODES.MULTI_CITY
    : GEO_MODES.SINGLE_CITY
}

// Сколько улиц показывать под городом и сколько остаётся за ссылкой.
export const localityStreets = (locality, mode, citiesCount) => {
  let limit = 0
  if (mode === GEO_MODES.SINGLE_CITY) limit = SINGLE_CITY_STREETS_LIMIT
  if (mode === GEO_MODES.MULTI_CITY && citiesCount <= CITIES_WITH_STREETS_MAX) {
    limit = STREETS_PER_CITY_LIMIT
  }
  if (limit === 0) return { streets: [], more: 0 }

  const sorted = [...(locality.streets || [])].sort(byHousesDesc)
  const streets = sorted.slice(0, limit)
  const total = Number.isFinite(Number(locality.streets_count))
    ? Number(locality.streets_count)
    : sorted.length
  return { streets, more: Math.max(total - streets.length, 0) }
}

// План показа блока: группы по странам, в каждой — города с улицами по правилу.
export const geographyPlan = (geography) => {
  const mode = geographyMode(geography)
  if (mode === GEO_MODES.EMPTY)
    return { mode, groups: [], allAddressesUuid: null }

  const countries = countriesWithCities(geography).sort(byDevicesDesc)
  const groups = countries.map((country) => {
    const localities = [...country.localities].sort(byDevicesDesc)
    return {
      country: {
        uuid: country.uuid,
        name: country.name,
        code: country.code,
        // ГИС отдаёт flag (ss_gis#370), в countries#index — flag_url
        flag: country.flag || country.flag_url || null,
        devices_count: country.devices_count,
      },
      showFlag: mode === GEO_MODES.MULTI_COUNTRY,
      localities: localities.map((locality, index) => ({
        uuid: locality.uuid,
        name: locality.name,
        full_name: locality.full_name,
        devices_count: locality.devices_count,
        streets_count: locality.streets_count,
        rank: index + 1,
        ...localityStreets(locality, mode, localities.length),
      })),
    }
  })

  return { mode, groups, allAddressesUuid: countries[0]?.uuid || null }
}

// --- строка устройства (раздел 5.8) ---

const DYNAMIC_KEYS_SHOWN = DYNAMIC_KEYS_STATES.filter(
  (state) => state !== 'unsupported'
)

// Модель третьей линии: null — линии нет. Отсутствие поля или null в ответе
// трактуется как «нет данных», и соответствующая часть не рисуется.
export const deviceDetails = (device) => {
  if (!device) return null
  const isCamera = device.type === 'camera'

  let firmware = null
  if (isCamera) {
    if (device.fw_version) {
      firmware = { prefix: 'FW', version: String(device.fw_version) }
    }
  } else {
    const status = FIRMWARE_STATUSES.includes(device.firmware_status)
      ? device.firmware_status
      : null
    const version = device.sw_version ? String(device.sw_version) : null
    if (status || version) {
      firmware = {
        prefix: 'SW',
        version,
        status,
        recommended:
          status === 'outdated' && device.recommended_sw
            ? String(device.recommended_sw)
            : null,
      }
    }
  }

  const dynamicKeys =
    !isCamera &&
    device.model_family === 'gos' &&
    DYNAMIC_KEYS_SHOWN.includes(device.dynamic_keys_state)
      ? device.dynamic_keys_state
      : null

  if (!firmware && !dynamicKeys) return null
  return { firmware, dynamicKeys }
}

// Цвет тега статуса: только отклонения получают цвет, остальное — серый текст.
export const firmwareTagColor = (status) => {
  if (status === 'outdated') return 'peach'
  if (status === 'beta') return 'lightblue'
  return null
}

export const dynamicKeysTagColor = (state) => {
  if (state === 'needs_firmware') return 'lightblue'
  if (state === 'available_off') return 'peach'
  if (state === 'on') return 'green'
  return null
}

const isInstalled = (device) =>
  Boolean(device?.installed ?? device?.is_installed)

// Куда ведёт строка: установленные — на адрес (как сейчас в DeviceSearchSuggestion),
// неустановленный домофон — в карточку по uuid, камера без адреса — никуда (шаг 6).
export const deviceRowLink = (device) => {
  if (!device) return { to: undefined, disabled: true, target: null }
  if (isInstalled(device))
    return { to: undefined, disabled: false, target: null }
  if (device.type === 'camera') {
    return { to: undefined, disabled: true, target: null }
  }
  return {
    to: `/intercom/${device.uuid}`,
    disabled: false,
    target: 'device',
    targetUuid: device.uuid,
  }
}

// Страница адреса устройства — как ссылка строки в DeviceSearchSuggestion:
// калитка → /gate/, устройство на доме → /h/, иначе подъезд /e/.
export const addressPath = (device) => {
  if (!device?.address_uuid) return null
  if (device.address_type === 'gate') return `/gate/${device.address_uuid}`
  if (device.address_type === 'house') return `/h/${device.address_uuid}`
  return `/e/${device.address_uuid}`
}

// «Недавно установленные» на мобильном: адрес без города (макет v3, таблица
// «блок → компонент»). short_address ГИС начинается с города: «Казань, ул. …».
export const addressWithoutCity = (address) => {
  if (!address) return address
  const parts = String(address).split(', ')
  return parts.length > 1 ? parts.slice(1).join(', ') : address
}

export const formatInstallationDate = (value, { short = false } = {}) => {
  if (!value) return ''
  const date = dayjs(value)
  if (!date.isValid()) return ''
  return date.format(short ? 'DD.MM' : 'DD.MM.YYYY')
}

// --- фильтры (раздел 5.6) ---

export const EMPTY_HOME_FILTERS = {
  [FILTER_NAMES.MODEL]: [],
  [FILTER_NAMES.FIRMWARE]: [],
  [FILTER_NAMES.DYNAMIC_KEYS]: [],
  [FILTER_NAMES.STABILITY]: [],
  [FILTER_NAMES.INSTALLED_SINCE]: [],
}

const uniq = (list) => Array.from(new Set(list))

// Выбранные модели → model_family[] и model_addon[] контракта. Фильтры ГИС
// складываются через И, поэтому надстройку шлём, только когда выбраны одни
// вложенные значения (IO 22, GOS 22, IO PRO): «IO» + «IO 22» — это все IO, а
// «GOS» + «IO 22» с model_addon[]=22 потеряли бы обычные GOS. Смешанный выбор
// даёт надмножество — лишние строки видно, пропавшие нет.
export const modelFilterParams = (selectedKeys = []) => {
  const options = MODEL_OPTIONS.filter((option) =>
    selectedKeys.includes(option.key)
  )
  const onlyNested = options.length > 0 && options.every((o) => o.addon)
  return {
    model_family: uniq(options.map((option) => option.family)),
    model_addon: onlyNested ? uniq(options.map((option) => option.addon)) : [],
  }
}

const nonEmpty = (list) =>
  Array.isArray(list) && list.length > 0 ? list : undefined

// Параметры запроса списка из состояния чипов; пустые — не отправляются.
export const homeFilterParams = (filters = EMPTY_HOME_FILTERS) => {
  const { model_family, model_addon } = modelFilterParams(
    filters[FILTER_NAMES.MODEL]
  )
  const installedSince = (filters[FILTER_NAMES.INSTALLED_SINCE] || []).find(
    (value) => INSTALLED_SINCE_VALUES.includes(value)
  )
  return {
    model_family: nonEmpty(model_family),
    model_addon: nonEmpty(model_addon),
    firmware_status: nonEmpty(filters[FILTER_NAMES.FIRMWARE]),
    dynamic_keys_state: nonEmpty(filters[FILTER_NAMES.DYNAMIC_KEYS]),
    stability_zone: nonEmpty(filters[FILTER_NAMES.STABILITY]),
    installed_since: installedSince,
  }
}

export const hasHomeFilters = (filters = EMPTY_HOME_FILTERS) =>
  Object.values(filters).some((values) => (values || []).length > 0)

// Подпись выбранного в чипе: одно значение — его название, больше — «(N)».
export const chipSelectionLabel = (selected = [], labelOf) => {
  if (selected.length === 0) return ''
  if (selected.length === 1) return labelOf(selected[0]) || ''
  return `(${selected.length})`
}

// Число в фасетах для значения чипа. Пока фасетов нет — undefined (значение
// без числа). ГИС не отдаёт значения без устройств, поэтому после ответа
// отсутствующий ключ — это 0. У вложенных моделей (IO 22, GOS 22, IO PRO)
// числа нет: фасет model_addon считается по надстройке без семейства, и
// «22» у IO и GOS неразличимы (в макете v3 у них тоже прочерк).
// Группы, которой нет в ответе (installed_since, stability_zone до #1489), —
// без чисел.
export const facetCount = (facets, filterName, option) => {
  if (!facets) return undefined
  if (filterName === FILTER_NAMES.MODEL) {
    if (option.addon || !facets.model_family) return undefined
    return facets.model_family[option.family] ?? 0
  }
  const group = facets[filterName]
  if (!group) return undefined
  return group[option.key] ?? 0
}

// Чипы, для которых ГИС считает фасеты (ss_gis#373).
export const FACET_CHIPS = [
  FILTER_NAMES.MODEL,
  FILTER_NAMES.FIRMWARE,
  FILTER_NAMES.DYNAMIC_KEYS,
]

// Статус онлайн новой главной — online_status[] (online|offline|unknown):
// «не в сети» — только false, null — «нет данных» (уточнение контракта 29.09).
export const onlineStatusParam = ({ online, offline, unknown } = {}) => {
  const values = []
  if (online) values.push('online')
  if (offline) values.push('offline')
  if (unknown) values.push('unknown')
  // все три значения — то же, что без фильтра
  return values.length > 0 && values.length < 3 ? values : undefined
}

export const deviceTypeParam = (deviceType = {}) => {
  if (deviceType.intercom && deviceType.camera) return undefined
  if (deviceType.intercom) return 'intercom'
  if (deviceType.camera) return 'camera'
  return undefined
}

export const isInstalledParam = ({ installed, uninstalled } = {}) => {
  if (installed && !uninstalled) return true
  if (uninstalled && !installed) return false
  return undefined
}

// Всё, что уходит в запрос списка и фасетов, кроме страницы и сортировки:
// старые чипы (filters) + новые (homeFilters) + поиск.
export const homeRequestParams = ({ filters = {}, homeFilters, query }) => ({
  search: query || undefined,
  device_type: deviceTypeParam(filters.device_type),
  is_installed: isInstalledParam(filters),
  online_status: onlineStatusParam(filters),
  ...homeFilterParams(homeFilters),
})

export const hasActiveRequestFilters = (params = {}) =>
  Object.entries(params).some(
    ([key, value]) => key !== 'search' && value !== undefined && value !== null
  )

// Счётчики строки «Найдено»: «нет данных» — только когда такие есть; на
// мобильном — только с цветной точкой (как в макете v3), подпись в Tooltip.
export const COUNTER_DOTS = {
  online: 'green',
  offline: 'pink',
  unknown: 'grey',
  outdated: 'yellow',
}

export const visibleCounterKeys = (counters, keys, { mobile = false } = {}) =>
  keys.filter((key) => {
    const value = counters?.[key]
    if (value === undefined || value === null) return false
    if (key === 'unknown' && !(value > 0)) return false
    if (mobile && !COUNTER_DOTS[key]) return false
    return true
  })

// Запрос без поиска и фильтров: его meta.total — размер парка компании.
export const isUnfilteredDevicesRequest = (arg = {}) => {
  const filters = arg.filters || {}
  const params = arg.params || {}
  const paramsActive = Object.values(params).some(
    (value) =>
      value !== undefined &&
      value !== null &&
      !(Array.isArray(value) && value.length === 0)
  )
  return (
    !(arg.search || '').trim() &&
    Number(arg.page || 1) === 1 &&
    !arg.address_uuid &&
    !arg.device_type &&
    filters.is_online === undefined &&
    filters.is_installed === undefined &&
    !paramsActive
  )
}
